import { arrayMove } from '@dnd-kit/sortable';
import { FunctionComponent, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageFile, Modal } from '../../../../shared';
import { OnCropCompleteArgs, useImageCrop, useIsNarrowViewport } from '../../../hooks';
import { GalleryItem } from '../../../models';
import { ImageCropModal } from '../../ImageCropModal';
import { ReorderArgs, SortableList, SortableMode } from '../../SortableList';
import { FileSettingsModal } from '../FileSettingsModal';
import { FilePreviewTeaser } from './FilePreviewTeaser';

import classNames from 'classnames';
import classes from './FilePreview.module.scss';

interface ImageGalleryItem extends GalleryItem {
  image: ImageFile;
}

interface FilePreviewProps {
  galleryItems: GalleryItem[];
  setGalleryItems?: (uploads: GalleryItem[]) => void;
  loading?: boolean;
  editableMedia?: boolean;
  hasSettings?: boolean;
  hideCaption?: boolean;
}

const isImageItem = (galleryItem: GalleryItem): galleryItem is ImageGalleryItem => {
  const { image, audio, video, pdf } = galleryItem;
  return Boolean(image && !audio && !video && !pdf);
};

export const FilePreview: FunctionComponent<FilePreviewProps> = memo(
  ({ galleryItems, setGalleryItems, loading, editableMedia = true, hasSettings, hideCaption }) => {
    const { t } = useTranslation();

    const isNarrowViewport = useIsNarrowViewport();

    const onCropComplete = useCallback(
      ({ previousImageId, image }: OnCropCompleteArgs) => {
        const galleryItemsCopy = [...galleryItems];

        const cropIndex = galleryItemsCopy.findIndex(
          ({ image: itemImage }) => itemImage?.id === previousImageId
        );

        if (cropIndex === -1) {
          return;
        }

        const { image: previousImage } = galleryItemsCopy[cropIndex];

        galleryItemsCopy[cropIndex] = {
          ...galleryItemsCopy[cropIndex],
          image: { ...previousImage, ...image } as ImageFile,
        };

        setGalleryItems?.(galleryItemsCopy);
      },
      [galleryItems, setGalleryItems]
    );

    const { fileToCrop, cropImageId, isCropUploading, openCrop, onCrop, onCropClose } =
      useImageCrop({ onCropComplete });

    const [teaserModalUrl, setTeaserModalUrl] = useState<string | null>(null);

    const [settingsIndex, setSettingsIndex] = useState<number | null>(null);

    const imageIds = useMemo(
      () => galleryItems.filter(isImageItem).map(({ image }) => image.id),
      [galleryItems]
    );

    const isImageDraggable = useMemo(
      () => !loading && imageIds.length > 1,
      [imageIds.length, loading]
    );

    const moveHandler = useCallback(
      ({ activeId, overId }: ReorderArgs) => {
        const activeIndex = galleryItems.findIndex(({ image }) => image?.id === activeId);

        const overIndex = galleryItems.findIndex(({ image }) => image?.id === overId);

        if (activeIndex === -1 || overIndex === -1) {
          return;
        }

        setGalleryItems?.(arrayMove(galleryItems, activeIndex, overIndex));
      },
      [galleryItems, setGalleryItems]
    );

    const removePreview = useCallback(
      (index: number) => {
        const galleryItemsCopy = [...galleryItems];

        galleryItemsCopy.splice(index, 1);

        setGalleryItems?.(galleryItemsCopy);

        if (index === settingsIndex) {
          setSettingsIndex(null);
        }
      },
      [galleryItems, setGalleryItems, settingsIndex]
    );

    const saveGalleryItem = useCallback(
      (galleryItem: GalleryItem) => {
        if (settingsIndex === null) {
          return;
        }

        const galleryItemsCopy = [...galleryItems];

        galleryItemsCopy[settingsIndex] = galleryItem;

        setGalleryItems?.(galleryItemsCopy);

        setSettingsIndex(null);
      },
      [galleryItems, setGalleryItems, settingsIndex]
    );

    if (!galleryItems.length) {
      return null;
    }

    return (
      <div className={classes['file-preview']}>
        <SortableList
          mode={SortableMode.GRID}
          items={imageIds}
          onReorder={moveHandler}
          disabled={!setGalleryItems || !isImageDraggable}
        >
          {galleryItems.map((item, index) => {
            const { image, audio, video, pdf } = item;

            return (
              <FilePreviewTeaser
                key={audio?.id ?? video?.id ?? pdf?.id ?? image?.id}
                item={item}
                index={index}
                isEditable={Boolean(setGalleryItems)}
                editableMedia={editableMedia}
                hasSettings={Boolean(hasSettings)}
                loading={Boolean(loading)}
                isCropping={isCropUploading && image?.id === cropImageId}
                isSettingsActive={settingsIndex === index}
                isImageDraggable={isImageDraggable}
                onPreviewOpen={setTeaserModalUrl}
                onCropOpen={openCrop}
                onSettingsOpen={setSettingsIndex}
                onRemove={removePreview}
              />
            );
          })}
        </SortableList>

        <Modal
          isOpen={Boolean(teaserModalUrl)}
          body={
            <img
              src={teaserModalUrl ?? ''}
              className={classNames(classes['file-preview__teaser-modal'], {
                [classes['file-preview__teaser-modal--narrow']]: isNarrowViewport,
              })}
              alt={''}
            />
          }
          onClose={() => setTeaserModalUrl(null)}
          root={'modal-preview-root'}
          ariaLabel={t('common.preview')}
          previewMode
        />

        {settingsIndex !== null && (
          <FileSettingsModal
            galleryItem={galleryItems[settingsIndex]}
            onClose={() => setSettingsIndex(null)}
            onSave={saveGalleryItem}
            hideCaption={hideCaption}
          />
        )}

        {fileToCrop && (
          <ImageCropModal
            file={fileToCrop}
            title={t('imageCrop.title')}
            onCrop={onCrop}
            onClose={onCropClose}
          />
        )}
      </div>
    );
  }
);
